import { useEffect, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import useTranslate from "@hooks/use_translate"
import { Alert, Stack, TextField, Typography } from "@mui/material"

export type ReasonDialogRequest = {
  title: string
  /** What will happen, in one or two sentences the operator can read before confirming. */
  summary: string
  /** Shown red: the change reaches customers immediately. */
  danger?: boolean
  onConfirm: (reason: string) => Promise<void>
}

type ReasonDialogProps = {
  request: ReasonDialogRequest | null
  onClose: () => void
}

/**
 * Every routing change is written to the history with who did it and why. The dialog is the one
 * place the reason is collected, so a change can never be made without one.
 */
export default function ReasonDialog({ request, onClose }: ReasonDialogProps) {
  const { translate } = useTranslate()
  const [reason, setReason] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (request) {
      setReason("")
      setLoading(false)
    }
  }, [request])

  const valid = reason.trim().length >= 3

  const confirm = async () => {
    if (!request || !valid) {
      return
    }
    setLoading(true)
    try {
      await request.onConfirm(reason.trim())
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogProvider
      title={request?.title ?? ""}
      isOpen={!!request}
      setClose={onClose}
      size="small"
      leftButton={{
        text: translate("ai_engine_cancel"),
        color: "secondary",
        onClick: onClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("ai_engine_confirm"),
        color: request?.danger ? "error" : "primary",
        onClick: confirm,
        loading,
        disabled: !valid
      }}
    >
      <Stack gap={2} pt={1}>
        <Alert severity={request?.danger ? "warning" : "info"}>
          {request?.summary}
        </Alert>
        <TextField
          label={translate("ai_engine_reason")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText={translate("ai_engine_reason_hint")}
          autoFocus
          multiline
          minRows={2}
          fullWidth
        />
        <Typography variant="caption" color="text.secondary">
          {translate("ai_engine_reason_history_note")}
        </Typography>
      </Stack>
    </DialogProvider>
  )
}
